import {
	type ReactNode,
	type Ref,
	forwardRef,
	memo,
	useCallback,
	useContext,
	useImperativeHandle,
	useRef,
	useSyncExternalStore,
} from "react";

import {
	TreeBranch,
	TreeContext,
	TreeItem,
	type TreeItemRenderProps,
	TreeProvider,
	useTreeBranch,
	useTreeRootContext,
} from "../core";
import {
	TreeDataContext,
	type UseBranchItems,
} from "../core/tree-data-context";
import type {
	MoveItemArgs,
	TreeItem as TreeItemType,
} from "../primitives/types";

export type TreeProps = {
	useBranchItems: UseBranchItems;
	renderItem: (props: TreeItemRenderProps) => ReactNode;
	renderDragPreview?: (item: TreeItemType) => ReactNode;
	indentPerLevel?: number;
	onMoveItem?: (args: MoveItemArgs) => void;
	onCreateFolder?: (
		parentBranchId: string | null,
		folder: TreeItemType,
	) => void;
	onDeleteFolder?: (folderId: string, branchId: string | null) => void;
	renderLoading?: () => ReactNode;
	onOpenStateChange?: (itemId: string, isOpen: boolean) => void;
	initialOpenState?: Record<string, boolean>;
	dragAndDropEnabled?: boolean;
};

function useIsItemOpen(itemId: string): boolean {
	const { subscribeItemOpen, getItemOpen } = useContext(TreeContext);
	const subscribe = useCallback(
		(onStoreChange: () => void) => subscribeItemOpen(itemId, onStoreChange),
		[subscribeItemOpen, itemId],
	);
	const getSnapshot = useCallback(
		() => getItemOpen(itemId),
		[getItemOpen, itemId],
	);
	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

const RecursiveItem = memo(function RecursiveItem({
	item,
	level,
	index,
	renderItem,
	renderDragPreview,
	indentPerLevel,
	onCreateFolder,
	onDeleteFolder,
	renderLoading,
}: {
	item: TreeItemType;
	level: number;
	index: number;
	renderItem: (props: TreeItemRenderProps) => ReactNode;
	renderDragPreview?: (item: TreeItemType) => ReactNode;
	indentPerLevel: number;
	onCreateFolder?: (
		parentBranchId: string | null,
		folder: TreeItemType,
	) => void;
	onDeleteFolder?: (folderId: string, branchId: string | null) => void;
	renderLoading?: () => ReactNode;
}) {
	const isOpen = useIsItemOpen(item.id);
	const isFolder = item.isFolder ?? false;

	const treeItem = (
		<TreeItem
			item={item}
			level={level}
			index={index}
			indentPerLevel={indentPerLevel}
			renderDragPreview={renderDragPreview}
		>
			{(props) => renderItem(props)}
		</TreeItem>
	);

	const branch = isOpen ? (
		<TreeBranch
			id={item.id}
			onCreateFolder={onCreateFolder}
			onDeleteFolder={onDeleteFolder}
		>
			{(branchChildren, isLoading) => (
				<>
					{branchChildren.map((child, i) => (
						<RecursiveItem
							key={child.id}
							item={child}
							level={level + 1}
							index={i}
							renderItem={renderItem}
							renderDragPreview={renderDragPreview}
							indentPerLevel={indentPerLevel}
							onCreateFolder={onCreateFolder}
							onDeleteFolder={onDeleteFolder}
							renderLoading={renderLoading}
						/>
					))}
					{isLoading && renderLoading?.()}
				</>
			)}
		</TreeBranch>
	) : null;

	const content =
		isFolder && isOpen ? (
			<div className="rounded-md bg-surface-subtle min-w-0">
				{treeItem}
				{branch}
			</div>
		) : (
			<>
				{treeItem}
				{branch}
			</>
		);

	return <div className="my-px min-w-0">{content}</div>;
});

export type TreeHandle = {
	createFolder: (folder: TreeItemType) => void;
	setItemOpen: (itemId: string, open: boolean) => void;
};

function TreeHandleBridge({
	handleRef,
}: {
	handleRef: Ref<TreeHandle>;
}) {
	const { createFolder } = useTreeBranch();
	const { setItemOpen } = useTreeRootContext();

	useImperativeHandle(
		handleRef,
		() => ({
			createFolder,
			setItemOpen,
		}),
		[createFolder, setItemOpen],
	);
	return null;
}

export const Tree = forwardRef<TreeHandle, TreeProps>(function Tree(
	{
		useBranchItems,
		renderItem,
		renderDragPreview,
		indentPerLevel = 20,
		onMoveItem,
		onCreateFolder,
		onDeleteFolder,
		renderLoading,
		onOpenStateChange,
		initialOpenState: initialOpenStateProp,
		dragAndDropEnabled = true,
	}: TreeProps,
	ref: Ref<TreeHandle>,
) {
	const initialOpenState = useRef(
		initialOpenStateProp
			? new Map(Object.entries(initialOpenStateProp))
			: undefined,
	).current;

	return (
		<TreeDataContext.Provider value={useBranchItems}>
			<TreeProvider
				initialOpenState={initialOpenState}
				onMoveItem={onMoveItem}
				onOpenStateChange={onOpenStateChange}
				dragAndDropEnabled={dragAndDropEnabled}
			>
				<TreeBranch
					id={null}
					onCreateFolder={onCreateFolder}
					onDeleteFolder={onDeleteFolder}
				>
					{(rootItems, isLoading) => (
						<>
							<TreeHandleBridge handleRef={ref} />
							{rootItems.map((item, index) => (
								<RecursiveItem
									key={item.id}
									item={item}
									level={0}
									index={index}
									renderItem={renderItem}
									renderDragPreview={renderDragPreview}
									indentPerLevel={indentPerLevel}
									onCreateFolder={onCreateFolder}
									onDeleteFolder={onDeleteFolder}
									renderLoading={renderLoading}
								/>
							))}
							{isLoading && renderLoading?.()}
						</>
					)}
				</TreeBranch>
			</TreeProvider>
		</TreeDataContext.Provider>
	);
});

export type { Instruction } from "@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item";
